// Admin → Analytics.
//
// Scope is deliberately narrow: the numbers the team actually asked for, each
// daily / weekly / monthly. Anything the backend cannot answer yet still gets a
// card, greyed, with the reason on it.
//
// Reading order matches how the question is asked: pick the subject (tabs),
// then narrow it (one filter line — frequency, then range, then the subject's
// own filters), then read the cards.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ChartCard, Series } from '../../compos/Analytics/charts';
import Dropdown from '../../compos/Analytics/Dropdown';
import { seriesColor } from '../../compos/Analytics/palette';
import {
	AuthorityRow,
	DayRange,
	Granularity,
	Point,
	Unit,
	bucketEnd,
	dayCount,
	fetchAuthorities,
	fetchTimeseries,
	formatByUnit,
	formatDay,
	istToday,
	lastNDays,
	METRICS,
} from '../../utils/analyticsApi';

type SeriesMap = Record<string, Point[]>;

type SectionKey = 'users' | 'app' | 'content' | 'reports' | 'website';

const SECTIONS: { key: SectionKey; label: string; hint: string }[] = [
	{
		key: 'users',
		label: 'Users',
		hint: 'How many people are joining: signups, and new app installs.',
	},
	{
		key: 'app',
		label: 'App',
		hint: 'How hard the app is used: opens, total time, and time per active user.',
	},
	{
		key: 'content',
		label: 'Content',
		hint: 'What people put into the feed: posts, comments, likes, and share links opened.',
	},
	{
		key: 'reports',
		label: 'Reports',
		hint: 'Reports filed — filter by the organisation or the specific authority they went to.',
	},
	{
		key: 'website',
		label: 'Website',
		hint: 'Traffic to b-reporter.com, and which authorities are opening it.',
	},
];

// Everything charted at the overall dimension, at the selected frequency —
// one request for the whole page (the endpoint takes up to 12 keys). The three
// report metrics are absent: they ride their own request so the Reports
// filters can swap in an authority/org dim without refetching all of this.
const CORE_METRICS = [
	'signups',
	'new_installs',
	'app_sessions',
	'app_time_seconds',
	'posts_created',
	'comments_created',
	'reactions_created',
	'shared_post_opens',
	'web_page_views',
	'web_unique_visitors',
];

const REPORT_METRICS = ['reports_created', 'reports_closed', 'reports_open'];

// Head-counts, always fetched by day. The API sums buckets, and summing a
// distinct count is meaningless: "4 authorities" on each of 30 days is not 120
// authorities, and a week's DAUs added together double-counts every repeat
// visitor. Weekly/monthly figures come from wau/mau instead, and
// authorities_active_web stays daily wherever it is shown.
const DAILY_METRICS = ['dau_app', 'wau', 'mau', 'authorities_active_web'];

const FREQUENCIES: { value: Granularity; label: string }[] = [
	{ value: 'day', label: 'Daily' },
	{ value: 'week', label: 'Weekly' },
	{ value: 'month', label: 'Monthly' },
];

const RANGES = [7, 30, 90, 180, 365].map((days) => ({
	value: String(days),
	label: `Last ${days} days`,
}));

/** Fixed colour per metric, so a metric keeps its hue across every section. */
const SLOT: Record<string, number> = {
	signups: 0,
	new_installs: 1,
	app_sessions: 0,
	app_time_seconds: 6,
	avg_time_per_user: 6,
	posts_created: 0,
	comments_created: 1,
	reactions_created: 2,
	shared_post_opens: 4,
	reports_created: 0,
	reports_closed: 5,
	reports_open: 1,
	web_page_views: 0,
	web_unique_visitors: 1,
	authorities_active_web: 6,
	authority_web_visits: 6,
};

const sum = (points: Point[]): number => points.reduce((n, p) => n + p.value, 0);
const mean = (points: Point[]): number => (points.length ? sum(points) / points.length : 0);
const median = (points: Point[]): number => {
	if (points.length === 0) return 0;
	const v = points.map((p) => p.value).sort((a, b) => a - b);
	const mid = Math.floor(v.length / 2);
	return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
};

const filterLabel = 'text-sm font-bold text-gray-500 ml-1';

const AnalyticsPanel = () => {
	const [section, setSection] = useState<SectionKey>('users');
	const [hoveredSection, setHoveredSection] = useState<SectionKey | null>(null);
	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

	const handleSectionMouseEnter = (key: SectionKey) => {
		if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
		hoverTimerRef.current = setTimeout(() => {
			setHoveredSection(key);
		}, 700);
	};

	const handleSectionMouseLeave = () => {
		if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
		setHoveredSection(null);
	};

	useEffect(() => {
		return () => {
			if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
		};
	}, []);

	const [granularity, setGranularity] = useState<Granularity>('day');
	const [rangeDays, setRangeDays] = useState(30);
	// Bumped every minute while the tab is visible: a new range object re-runs
	// every fetch below. The backend recomputes today at most once a minute, so
	// polling faster would only re-read the same numbers.
	const [liveTick, setLiveTick] = useState(0);
	useEffect(() => {
		const id = setInterval(() => {
			if (document.visibilityState === 'visible') setLiveTick((n) => n + 1);
		}, 60_000);
		return () => clearInterval(id);
	}, []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const range = useMemo<DayRange>(() => lastNDays(rangeDays), [rangeDays, liveTick]);
	// Organisation narrows the authority list; a chosen authority wins, since an
	// authority already sits inside exactly one organisation. Keeping them
	// linked this way means the pair always collapses to the single `dim` the
	// API accepts, and the two can never contradict each other.
	const [reportsOrg, setReportsOrg] = useState<string>('');
	const [reportsAuthority, setReportsAuthority] = useState<string>('');
	const [webOrg, setWebOrg] = useState<string>('');
	const [webAuthority, setWebAuthority] = useState<string>('');
	const [reloadKey, setReloadKey] = useState(0);

	const [core, setCore] = useState<SeriesMap>({});
	const [daily, setDaily] = useState<SeriesMap>({});
	const [reports, setReports] = useState<SeriesMap>({});
	const [authorityVisits, setAuthorityVisits] = useState<Point[] | null>(null);
	const [authorities, setAuthorities] = useState<AuthorityRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fail = useCallback((err: any) => {
		if (err?.name !== 'AbortError') setError(err?.message ?? 'Could not load analytics.');
	}, []);

	// Everything the cards chart, plus the day-level head-counts.
	useEffect(() => {
		const ctrl = new AbortController();
		setLoading(true);
		Promise.all([
			fetchTimeseries(CORE_METRICS, range, { granularity }, ctrl.signal),
			fetchTimeseries(DAILY_METRICS, range, { granularity: 'day' }, ctrl.signal),
		])
			.then(([c, d]) => {
				setCore(c.series);
				setDaily(d.series);
				setError(null);
			})
			.catch(fail)
			.finally(() => {
				if (!ctrl.signal.aborted) setLoading(false);
			});
		return () => ctrl.abort();
	}, [range, granularity, reloadKey, fail]);

	// Authority / organisation lists for the filters.
	useEffect(() => {
		const ctrl = new AbortController();
		fetchAuthorities(range, ctrl.signal)
			.then((res) => setAuthorities(res.rows))
			.catch(fail);
		return () => ctrl.abort();
	}, [range, reloadKey, fail]);

	// Filed / closed / open, narrowed to one authority or to every authority in
	// one organisation ('' = overall). Separate from the core fetch so changing
	// this filter reloads three series, not the whole page.
	const reportsDimParam = reportsAuthority || (reportsOrg ? `org:${reportsOrg}` : '');
	useEffect(() => {
		const ctrl = new AbortController();
		fetchTimeseries(REPORT_METRICS, range, { dim: reportsDimParam, granularity }, ctrl.signal)
			.then((res) => setReports(res.series))
			.catch(fail);
		return () => ctrl.abort();
	}, [reportsDimParam, range, granularity, reloadKey, fail]);

	// Website visits for one authority.
	useEffect(() => {
		if (!webAuthority) {
			setAuthorityVisits(null);
			return;
		}
		const ctrl = new AbortController();
		fetchTimeseries(['authority_web_visits'], range, { dim: webAuthority, granularity }, ctrl.signal)
			.then((res) => setAuthorityVisits(res.series['authority_web_visits'] ?? []))
			.catch(fail);
		return () => ctrl.abort();
	}, [webAuthority, range, granularity, reloadKey, fail]);

	const organisations = useMemo(
		() =>
			Array.from(
				new Set(authorities.map((a) => a.organisation).filter((o): o is string => !!o))
			).sort(),
		[authorities]
	);

	/**
	 * Average time one active user spent in the app during each bucket:
	 * total foreground seconds ÷ the unique actives for that same window
	 * (DAU for a day, the WAU ending on the bucket's last day for a week, MAU
	 * for a month). Summing DAU across a week would double-count anyone who
	 * opened the app twice, which is exactly what this has to avoid.
	 */
	const avgTimePerUser = useMemo<Point[]>(() => {
		const time = core['app_time_seconds'] ?? [];
		const key = granularity === 'day' ? 'dau_app' : granularity === 'week' ? 'wau' : 'mau';
		const byDay = new Map((daily[key] ?? []).map((p) => [p.day, p.value]));
		return time.map((p) => {
			const users = byDay.get(bucketEnd(p.day, granularity, range.to)) ?? 0;
			return { day: p.day, value: users > 0 ? Math.round(p.value / users) : 0 };
		});
	}, [core, daily, granularity, range.to]);

	const makeSeries = (key: string, points: Point[], label?: string): Series[] => [
		{
			key,
			label: label ?? METRICS[key]?.label ?? key,
			color: seriesColor(SLOT[key] ?? 0),
			points,
		},
	];

	const periodWord = granularity === 'day' ? 'day' : granularity === 'week' ? 'week' : 'month';

	/** Every card is the same shape: headline for the range, then the trend. */
	const card = (
		key: string,
		opts: {
			title?: string;
			subtitle?: string;
			points?: Point[];
			series?: Series[];
			stat?: { value: string; caption: string };
			unit?: Unit;
			/** Overrides the filter row — only for series that must stay daily. */
			granularity?: Granularity;
		} = {}
	) => {
		const meta = METRICS[key];
		const unit = opts.unit ?? meta?.unit ?? 'count';
		const points = opts.points ?? core[key] ?? [];
		const series = opts.series ?? makeSeries(key, points, opts.title);
		return (
			<ChartCard
				key={key}
				title={opts.title ?? meta?.label ?? key}
				subtitle={opts.subtitle}
				series={series}
				unit={unit}
				granularity={opts.granularity ?? granularity}
				loading={loading}
				notTracked={meta?.notTracked}
				emptyNote={meta?.pending ?? 'Nothing recorded in this range.'}
				stat={
					opts.stat ?? {
						value: formatByUnit(sum(points), unit),
						caption: `total across ${dayCount(range)} days`,
					}
				}
			/>
		);
	};

	// Names come straight from the DB and some carry trailing spaces.
	const authorityName = (id: string) =>
		authorities.find((a) => String(a.authorityId) === id)?.name.trim() ?? 'this authority';

	const reportsTarget = reportsAuthority
		? authorityName(reportsAuthority)
		: reportsOrg
			? `every authority in ${reportsOrg}`
			: 'all authorities';
	const openPoints = reports['reports_open'] ?? [];
	const openNow = openPoints[openPoints.length - 1]?.value ?? 0;

	// Authority options, narrowed by whichever organisation is selected. The
	// organisation is only ever a way to find an authority faster — the metric
	// itself is decided by the authority once one is picked.
	const authorityOptions = (org: string) => [
		{ value: '', label: 'All authorities' },
		...(org ? authorities.filter((a) => a.organisation === org) : authorities).map((a) => ({
			value: String(a.authorityId),
			label: a.name.trim(),
		})),
	];
	const orgOptions = [
		{ value: '', label: 'All organisations' },
		...organisations.map((o) => ({ value: o, label: o })),
	];
	/** Clearing/changing an organisation drops an authority that left the list. */
	const keepsAuthority = (org: string, authorityId: string) =>
		!org ||
		!authorityId ||
		authorities.some((a) => String(a.authorityId) === authorityId && a.organisation === org);

	return (
		<div className='max-w-6xl mx-auto px-4 pb-5'>
			<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>View Analytics</h1>

			{/* 1. What are we looking at? (5-part sliding socket selector) */}
			{(() => {
				const sectionIndex = SECTIONS.findIndex((s) => s.key === section);
				return (
					<div className='relative z-50 bg-gray-100/80 border border-gray-200 rounded-2xl flex items-center h-[52px] p-1.5 mb-4 backdrop-blur-sm shadow-sm'>
						{/* Sliding Background Indicator */}
						<div
							className='absolute top-1.5 bottom-1.5 bg-white shadow-md border border-gray-200/60 rounded-xl transition-all duration-300 ease-out z-0 pointer-events-none'
							style={{
								left: `calc(${sectionIndex * 20}% + 6px)`,
								width: 'calc(20% - 12px)',
							}}
						/>

						{SECTIONS.map((s) => {
							const isHovered = hoveredSection === s.key;

							return (
								<div
									key={s.key}
									className='relative z-10 w-1/5 h-full flex items-center justify-center'
									onMouseEnter={() => handleSectionMouseEnter(s.key)}
									onMouseLeave={handleSectionMouseLeave}
								>
									<button
										type='button'
										onClick={() => {
											setSection(s.key);
											window.scrollTo({ top: 0, behavior: 'smooth' });
										}}
										className={`w-full h-full font-bold text-sm transition-colors duration-300 flex items-center justify-center rounded-xl outline-none focus:outline-none ${section === s.key
											? 'text-blue-700'
											: 'text-gray-500 hover:text-gray-900'
											}`}
									>
										{s.label}
									</button>

									{/* Custom instant tooltip box & pointer combo - centered under each segment */}
									{isHovered && (
										<div className='absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 p-3.5 bg-blue-950 text-blue-100 rounded-2xl shadow-xl border border-blue-900/80 z-[120] pointer-events-none text-left'>
											<div className='absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-blue-950 border-t border-l border-blue-900/80' />
											<p className='text-xs font-semibold text-blue-50/90 leading-relaxed m-0'>
												{s.hint}
											</p>
										</div>
									)}
								</div>
							);
						})}
					</div>
				);
			})()}

			{/* 2. One line of filters: frequency, then range, then this section's own. */}
			<div className='bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap items-center gap-2 mb-6'>
				<span className={filterLabel}>Frequency:</span>
				<Dropdown
					ariaLabel='Frequency'
					value={granularity}
					options={FREQUENCIES}
					onChange={(v) => setGranularity(v as Granularity)}
				/>

				<span className={filterLabel}>Range:</span>
				<Dropdown
					ariaLabel='Range'
					value={String(rangeDays)}
					options={RANGES}
					onChange={(v) => setRangeDays(Number(v))}
				/>

				{section === 'reports' && (
					<>
						<span className={filterLabel}>Organisation:</span>
						<Dropdown
							ariaLabel='Organisation'
							value={reportsOrg}
							options={orgOptions}
							onChange={(org) => {
								setReportsOrg(org);
								if (!keepsAuthority(org, reportsAuthority)) setReportsAuthority('');
							}}
						/>

						<span className={filterLabel}>Authority:</span>
						<Dropdown
							ariaLabel='Authority'
							value={reportsAuthority}
							options={authorityOptions(reportsOrg)}
							onChange={setReportsAuthority}
						/>
					</>
				)}

				{section === 'website' && (
					<>
						<span className={filterLabel}>Organisation:</span>
						<Dropdown
							ariaLabel='Organisation'
							value={webOrg}
							options={orgOptions}
							onChange={(org) => {
								setWebOrg(org);
								if (!keepsAuthority(org, webAuthority)) setWebAuthority('');
							}}
						/>

						<span className={filterLabel}>Authority:</span>
						<Dropdown
							ariaLabel='Authority'
							value={webAuthority}
							options={authorityOptions(webOrg)}
							onChange={setWebAuthority}
						/>
					</>
				)}

				<button
					type='button'
					onClick={() => setReloadKey((k) => k + 1)}
					title='Reload'
					aria-label='Reload analytics'
					className='ml-auto rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700'
				>
					<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
				</button>
			</div>

			{error && (
				<div className='mb-6 flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700'>
					<AlertTriangle className='h-4 w-4 shrink-0' />
					{error}
				</div>
			)}

			<div className='grid grid-cols-1 gap-5 xl:grid-cols-2'>
				{section === 'users' && (
					<>
						{card('signups', { subtitle: 'New accounts created' })}
						{card('new_installs', {
							subtitle: 'First-ever app opens — one per new device',
						})}
					</>
				)}

				{section === 'app' && (
					<>
						{card('app_sessions', { subtitle: 'Times the app was brought to the foreground' })}
						{card('app_time_seconds', { subtitle: 'Foreground time across all users' })}
						{card('avg_time_per_user', {
							title: 'Avg time per user',
							subtitle: `App time ÷ unique users active in the same ${periodWord}`,
							points: avgTimePerUser,
							unit: 'seconds',
							series: makeSeries('avg_time_per_user', avgTimePerUser, 'Avg time per user'),
							stat: {
								value: formatByUnit(mean(avgTimePerUser), 'seconds'),
								caption: `average per active user per ${periodWord}`,
							},
						})}
					</>
				)}

				{section === 'content' && (
					<>
						{card('posts_created', { subtitle: 'Posts published' })}
						{card('comments_created', { subtitle: 'Comments left on posts' })}
						{card('reactions_created', { subtitle: 'Likes on posts' })}
						{card('shared_post_opens', {
							title: 'Post shares opened',
							subtitle: 'Share links opened by a recipient',
						})}
					</>
				)}

				{section === 'reports' && (
					<>
						{card('reports_created', {
							subtitle: `Filed to ${reportsTarget}`,
							points: reports['reports_created'] ?? [],
							series: makeSeries('reports_created', reports['reports_created'] ?? []),
						})}
						{card('reports_closed', {
							subtitle: `Closed by ${reportsTarget}`,
							points: reports['reports_closed'] ?? [],
							series: makeSeries('reports_closed', reports['reports_closed'] ?? []),
						})}
						{card('reports_open', {
							subtitle: `Still open with ${reportsTarget} — a running total, so the line shows the backlog growing or shrinking`,
							points: openPoints,
							series: makeSeries('reports_open', openPoints),
							// A level, not a volume: the range total is meaningless, and
							// weekly/monthly buckets already hold the end-of-period value.
							stat: {
								value: formatByUnit(openNow, 'count'),
								caption: `open as of ${formatDay(range.to)}`,
							},
						})}
					</>
				)}

				{section === 'website' && (
					<>
						{card('web_page_views', {
							title: 'Website opens',
							subtitle: 'Page views, with the unique visitors behind them',
							series: [
								{
									key: 'web_page_views',
									label: 'Page views',
									color: seriesColor(SLOT.web_page_views),
									points: core['web_page_views'] ?? [],
								},
								{
									key: 'web_unique_visitors',
									label: 'Unique visitors',
									color: seriesColor(SLOT.web_unique_visitors),
									points: core['web_unique_visitors'] ?? [],
								},
							],
							stat: {
								value: formatByUnit(sum(core['web_page_views'] ?? []), 'count'),
								caption: `page views across ${dayCount(range)} days`,
							},
						})}
						{webAuthority
							? card('authority_web_visits', {
								title: 'Website visits',
								subtitle: `Days ${authorityName(webAuthority)} opened the site`,
								points: authorityVisits ?? [],
								series: makeSeries('authority_web_visits', authorityVisits ?? [], 'Website visits'),
								stat: {
									value: formatByUnit(sum(authorityVisits ?? []), 'count'),
									caption: `days with a visit, out of ${dayCount(range)}`,
								},
							})
							: card('authorities_active_web', {
								subtitle:
									'Distinct authorities who opened the site. Always per day — a headcount cannot be added up into weeks.',
								// Series and headline both come from the day-level fetch: 30
								// days of "4 authorities" is not 120 authorities. The median
								// is the typical day, unmoved by one dead weekend or one
								// unusually busy Monday.
								points: daily['authorities_active_web'] ?? [],
								series: makeSeries(
									'authorities_active_web',
									daily['authorities_active_web'] ?? [],
									'Authorities'
								),
								granularity: 'day',
								stat: {
									value: formatByUnit(median(daily['authorities_active_web'] ?? []), 'count'),
									caption: 'median per day',
								},
							})}
					</>
				)}
			</div>

			<p className='mt-8 text-center text-xs text-gray-400'>
				Live through {formatDay(istToday(), true)} (IST) — today and yesterday refresh every minute.
			</p>
		</div>
	);
};

export default AnalyticsPanel;
